<script setup lang="ts">
import { computed, watch } from 'vue'
import { useField } from 'vee-validate'
import { getLocalizedText } from '../utils/formHelpers'
import FormFieldWrapper from '../FormFieldWrapper.vue'
import type { FormField, SelectOption } from "../types/formTypeStructure";
import { AlertCircle } from "lucide-vue-next";

interface CheckboxFormField extends FormField {
    fieldType: "checkbox";
    selectOptions: SelectOption[];
}

interface CheckboxFormFieldProps {
    field: CheckboxFormField;
    name?: string; 
    currentLang?: string;
    defaultValue?: string | string[]; 
    className?: string;
}

const props = defineProps<CheckboxFormFieldProps>()
const emit = defineEmits<{
  'update:modelValue': [value: string[]];
  'change': [value: string[]];
  'input': [value: string[]];
}>();

const fieldName = computed(() => props.name ?? props.field.fieldName.toString())
const currentLang = computed(() => props.currentLang ?? 'fr')
const label = computed(() => getLocalizedText(props.field.label, currentLang.value))
const isRequired = computed(() => props.field.validations?.some(v => v.validationType === 'required'))

const normalizedDefaultValue = computed(() => {
  if (Array.isArray(props.defaultValue)) return props.defaultValue;
  if (typeof props.defaultValue === 'string') return [props.defaultValue];
  return [];
});

const {
  value,
  errorMessage,
  meta,
  setTouched,
  handleChange
} = useField<string[]>(fieldName.value, undefined, {
  initialValue: normalizedDefaultValue.value,
})

// Émettre les événements quand la valeur change
watch(value, (newValue) => {
  emit('update:modelValue', newValue);
  emit('change', newValue);
  emit('input', newValue);
});

const isChecked = (val: string) => {
  return Array.isArray(value.value) && value.value.includes(val)
}

const toggleValue = (val: string, checked: boolean) => {
  const updated = checked
    ? [...value.value, val]
    : value.value.filter(v => v !== val)

  handleChange(updated)
  setTouched(true)
}
</script>

<template>
  <div class="checkbox-field-container">
    <!-- Label dupliqué au-dessus des checkboxes -->
    <div class="checkbox-field-label-container">
      <span class="checkbox-field-label-text">
        {{ label }}
        <AlertCircle
          v-if="isRequired"
          :size="16"
          class="inline text-red-500 ml-2"
        />
      </span>
    </div>

    <FormFieldWrapper :field="field" :current-lang="currentLang">
      <div class="flex flex-col gap-3" :class="className">
        <label
          v-for="(option, idx) in field.selectOptions"
          :key="`${fieldName}-${option.value}-${idx}`"
          :for="`${fieldName}-${option.value}-${idx}`"
          class="relative flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:border-[#04406E] hover:bg-[#e6f0f5] transition-all duration-200 group"
        >
          <div class="relative flex-shrink-0">
            <input
              type="checkbox"
              :id="`${fieldName}-${option.value}-${idx}`"
              :value="option.value"
              :checked="isChecked(option.value)"
              @change="(e: Event) => toggleValue(option.value, (e.target as HTMLInputElement).checked)"
              class="appearance-none w-4 h-4 border-2 border-gray-300 rounded transition-all duration-200 focus:ring-2 focus:ring-[#04406E] focus:ring-offset-2 checked:bg-[#04406E] checked:border-[#04406E]"
            />
            <!-- Custom checkbox -->
            <div 
              class="absolute inset-0 flex items-center justify-center pointer-events-none"
              :class="isChecked(option.value) ? 'opacity-100' : 'opacity-0'"
            >
              <svg 
                class="w-3 h-3 text-white" 
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path 
                  fill-rule="evenodd" 
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                  clip-rule="evenodd" 
                />
              </svg>
            </div>
          </div>
          <span class="text-gray-700 font-medium group-hover:text-[#04406E] transition-colors duration-200">
            {{ getLocalizedText(option.label, currentLang) }}
          </span>
        </label>
      </div>

      <p v-if="meta.touched && errorMessage" class="text-sm text-red-500 mt-2">
        {{ errorMessage }}
      </p>
    </FormFieldWrapper>
  </div>
</template>

<style scoped>
.checkbox-field-container {
  position: relative;
  display: block;
  width: 100%;
}

.checkbox-field-label-container {
  position: relative;
  display: block;
  margin-bottom: 0.75rem;
  z-index: 100;
}

.checkbox-field-label-text {
  display: block;
  font-size: 1.125rem;
  font-weight: 700;
  color: #333333;
  line-height: 1.2;
  margin: 0;
  padding: 0;
  position: static !important;
  transform: none !important;
  top: auto !important;
  left: auto !important;
  right: auto !important;
  bottom: auto !important;
  height: auto !important;
  overflow: visible !important;
  text-align: left !important;
  text-overflow: unset !important;
  white-space: normal !important;
  pointer-events: auto !important;
  border: none !important;
  transform-origin: unset !important;
  transition: none !important;
}
</style>
